import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import {
  IChatFormField,
  IChatForm,
  IChatFormResolvedEvent,
  ChatFormFieldType,
  ChatFormOperation,
} from '@nimbus-global/features/chat/models/chat-form.model';
import { IChatFormFieldAction, IChatFormResolver } from '@nimbus-global/features/chat/models/chat-form-resolver.model';
import { IOptionItem } from '@nimbus-library/features/shared/models/interfaces/i-option-item';

@UntilDestroy()
@Component({
  selector: 'nimbus-chat-message-form',
  standalone: false,
  templateUrl: './chat-message-form.component.html',
  styleUrl: './chat-message-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageFormComponent implements OnInit, OnChanges {
  @Input()
  public chatForm: IChatForm;
  @Input()
  public formResolver: IChatFormResolver;

  @Output()
  public resolved = new EventEmitter<IChatFormResolvedEvent>();
  @Output()
  public formActionInvoked = new EventEmitter<IChatFormFieldAction>();

  public readonly declineComment = new UntypedFormControl('');

  private readonly requestsByField: Record<string, Subject<void>> = {};

  public readonly TranslationKeys = TranslationKeys;
  public readonly ICONS = ICONS;
  public readonly ChatFormFieldType = ChatFormFieldType;
  public readonly ChatFormOperation = ChatFormOperation;

  public form: UntypedFormGroup;
  public optionsByField: Record<string, ReplaySubject<IOptionItem[]>> = {};
  public fieldValues$: Record<string, Observable<string>> = {};

  public getFieldAction(field: IChatFormField): IChatFormFieldAction | null {
    if (!this.formResolver?.resolveFieldAction) {
      return null;
    }
    const currentValues = this.chatForm?.values ?? this.form?.getRawValue();
    return this.formResolver.resolveFieldAction(this.chatForm.entity, field.name, currentValues);
  }

  public getFieldMaxLength(field: IChatFormField): number | null {
    return this.formResolver?.resolveFieldMaxLength?.(this.chatForm.entity, field.name) ?? null;
  }

  public ngOnInit(): void {
    this.buildForm();
    this.bindFormState();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) {
      return;
    }
    if (changes.chatForm) {
      const prev = changes.chatForm.previousValue as IChatForm;
      const curr = changes.chatForm.currentValue as IChatForm;
      if (prev?.fields !== curr?.fields) {
        this.buildForm();
      }
      this.bindFormState();
    }
  }

  public get formTitle(): TranslationKeys | null {
    return this.formResolver?.resolveTitle?.(this.chatForm?.entity, this.chatForm?.operation) ?? null;
  }

  public get formDescription(): TranslationKeys | null {
    return this.formResolver?.resolveDescription?.(this.chatForm?.entity, this.chatForm?.operation) ?? null;
  }

  public get isResolved(): boolean {
    return this.chatForm?.values !== undefined;
  }

  public get isSubmitting(): boolean {
    return !!this.chatForm?.isSubmitting;
  }

  public get submitLabel(): TranslationKeys {
    return this.chatForm?.operation === ChatFormOperation.Confirm ? TranslationKeys.GeneralApprove : TranslationKeys.ChatWidgetFormSubmit;
  }

  public onSubmit(): void {
    if (this.form.invalid || this.isResolved || this.isSubmitting) {
      return;
    }
    this.resolved.emit({ values: this.form.getRawValue() });
  }

  public onReject(): void {
    if (this.isResolved || this.isSubmitting) {
      return;
    }
    const declineReason = (this.declineComment.value as string | null)?.trim() || undefined;
    this.resolved.emit({ declineReason });
  }

  private buildForm(): void {
    const controls: Record<string, UntypedFormControl> = {};
    this.fieldValues$ = {};

    for (const field of this.chatForm?.fields ?? []) {
      const maxLength = this.getFieldMaxLength(field);
      const validators = [...(field.required ? [Validators.required] : []), ...(maxLength ? [Validators.maxLength(maxLength)] : [])];
      controls[field.name] = new UntypedFormControl(null, validators);

      if (field.type === ChatFormFieldType.Reference && field.referenceType && this.formResolver?.resolveReferenceOptions) {
        this.loadFormFieldReferenceOptions(field);
      }

      this.fieldValues$[field.name] = this.buildFieldValue$(field);
    }

    this.form = new UntypedFormGroup(controls);
    this.bindCascadeTriggers();
    this.bindFormValueUpdate();
  }

  private buildFieldValue$(field: IChatFormField): Observable<string> {
    const value = this.chatForm?.values?.[field.name];
    if (value == null || value === '') {
      return of('');
    }
    if (field.type === ChatFormFieldType.Reference) {
      const subject = this.optionsByField[field.name];
      if (subject) {
        return subject.pipe(map((options) => options.find((o) => o.id === value)?.name ?? String(value)));
      }
    }
    return of(String(value));
  }

  private bindFormValueUpdate(): void {
    if (!this.formResolver?.observeFormValueUpdates || !this.chatForm) {
      return;
    }
    this.formResolver
      .observeFormValueUpdates(this.chatForm.entity)
      .pipe(untilDestroyed(this))
      .subscribe((update) => {
        if (this.isResolved || this.isSubmitting) {
          return;
        }
        const currentValues = this.form.getRawValue() as Record<string, unknown>;
        const matches = !update.requiresValues || Object.entries(update.requiresValues).every(([k, v]) => currentValues[k] === v);
        if (matches) {
          this.form.patchValue(update.values);
        }
      });
  }

  private bindCascadeTriggers(): void {
    const triggers = (this.chatForm?.fields ?? []).filter((f) => f.isCascade && f.type === ChatFormFieldType.Reference);
    for (const trigger of triggers) {
      this.form
        .get(trigger.name)
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe(() => this.onCascadeTriggerChanged(trigger.name));
    }
  }

  private onCascadeTriggerChanged(triggerName: string): void {
    const dependents = (this.chatForm?.fields ?? []).filter(
      (f) => f.type === ChatFormFieldType.Reference && f.referenceType && f.name !== triggerName,
    );
    for (const dependent of dependents) {
      this.form.get(dependent.name)?.reset(null, { emitEvent: false });
      this.loadFormFieldReferenceOptions(dependent);
    }
  }

  private loadFormFieldReferenceOptions(field: IChatFormField): void {
    const existingOptions$ = this.optionsByField[field.name];
    const options$ = existingOptions$ ?? new ReplaySubject<IOptionItem[]>(1);
    this.optionsByField[field.name] = options$;

    let request$ = this.requestsByField[field.name];
    if (!request$) {
      request$ = new Subject<void>();
      this.requestsByField[field.name] = request$;
      request$
        .pipe(
          switchMap(() =>
            this.formResolver.resolveReferenceOptions(
              field.referenceType,
              this.chatForm,
              this.chatForm?.values ?? this.form?.getRawValue(),
            ),
          ),
          untilDestroyed(this),
        )
        .subscribe((options) => options$.next(options));
    }
    if (existingOptions$) {
      options$.next([]);
    }
    request$.next();
  }

  private bindFormState(): void {
    if (!this.form || !this.chatForm) {
      return;
    }

    if (this.isResolved || this.isSubmitting) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }

    if (!this.isResolved) {
      return;
    }

    this.form.patchValue(this.chatForm.values ?? {}, { emitEvent: false });
  }
}
